import React, { useCallback, useEffect, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Row, Col, Card, CardBody, Button, Nav, NavItem } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import "../../assets/custom css/loadoverlay.scss";
import AdminNotificationsService from "../../services/adminNotifications.service";

const PAGE_SIZE = 20;

const formatTime = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "";
  }
};

const AdminNotifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authData = useSelector((state) => state.authReducer.authData);
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const adminUserId = authData?.userData?._id || storedUser?._id;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  const loadNotifications = useCallback(async (pageIndex = 1) => {
    if (!adminUserId) return;
    setLoading(true);
    try {
      const offset = (pageIndex - 1) * PAGE_SIZE;
      const response = await AdminNotificationsService.getAdminNotifications(PAGE_SIZE, offset);
      if (response.data?.status) {
        setNotifications(response.data.data || []);
        setUnreadCount(response.data.count?.unread_count || 0);
        setTotalCount(response.data.count?.count || 0);
      }
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [adminUserId]);

  useEffect(() => {
    loadNotifications(page);
  }, [page, loadNotifications]);

  const handleNotificationClick = async (notification) => {
    if (!notification?.is_read_for_admin) {
      try {
        await AdminNotificationsService.markAsRead(notification._id);
        setNotifications((prev) =>
          prev.map((item) =>
            item._id === notification._id
              ? { ...item, is_read_for_admin: true }
              : item
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }
    if (notification?.redirectTo) {
      navigate(notification.redirectTo);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await AdminNotificationsService.markAllAsRead();
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, is_read_for_admin: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <TransitionGroup>
            <CSSTransition
              component="div"
              classNames="TabsAnimation"
              appear
              timeout={1500}
              enter={false}
              exit={false}
            >
              <div>
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-bell icon-gradient bg-happy-green" />
                      </div>
                      <div>
                        {t("Notifications")}
                        <div className="page-title-subheading">
                          {t("View all your notifications. Click on a notification to open it.")}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions d-flex gap-2">
                      {unreadCount > 0 && (
                        <Button color="success" className="btn-hover-shine" onClick={handleMarkAllRead}>
                          {t("Mark all read")}
                        </Button>
                      )}
                      <Button color="outline-success" className="btn-hover-shine" onClick={() => navigate(-1)}>
                        {t("Back")}
                      </Button>
                    </div>
                  </div>
                </div>
                <Row>
                  <Col md="12">
                    <Card className="main-card mb-3">
                      <CardBody>
                        <LoadingOverlay
                          tag="div"
                          active={loading}
                          styles={{ overlay: (base) => ({ ...base }) }}
                          spinner={<DNALoader />}
                        >
                          {notifications.length === 0 && !loading ? (
                            <div className="text-center text-muted py-5">
                              {t("No notifications yet")}
                            </div>
                          ) : (
                            <Nav vertical>
                              {notifications.map((notification) => (
                                <NavItem
                                  key={notification._id}
                                  className="border-bottom p-3"
                                  style={{
                                    cursor: "pointer",
                                    background: notification.is_read_for_admin ? "transparent" : "#f0fdf4",
                                  }}
                                  onClick={() => handleNotificationClick(notification)}
                                >
                                  <div className="d-flex align-items-start">
                                    <div className="me-3">
                                      <div
                                        className="bg-success rounded-circle d-flex align-items-center justify-content-center"
                                        style={{ width: "40px", height: "40px" }}
                                      >
                                        <FontAwesomeIcon icon={faBell} className="text-white" size="sm" />
                                      </div>
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="fw-bold">{notification.title}</span>
                                        {!notification.is_read_for_admin && (
                                          <span className="badge bg-success">{t("unread")}</span>
                                        )}
                                      </div>
                                      <div className="text-muted small mt-1">{notification.description}</div>
                                      <div className="text-muted small">{formatTime(notification.action_time)}</div>
                                    </div>
                                  </div>
                                </NavItem>
                              ))}
                            </Nav>
                          )}
                          {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                              <Button
                                color="outline-secondary"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                              >
                                {t("Previous")}
                              </Button>
                              <span className="text-muted small">
                                {t("Page")} {page} {t("of")} {totalPages}
                              </span>
                              <Button
                                color="outline-secondary"
                                size="sm"
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                              >
                                {t("Next")}
                              </Button>
                            </div>
                          )}
                        </LoadingOverlay>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </div>
            </CSSTransition>
          </TransitionGroup>
        </div>
      </div>
    </Fragment>
  );
};

export default AdminNotifications;
